import {Injectable} from '@angular/core';
import * as bigInt from 'big-integer';



@Injectable()
export class DevAddrUtils {
  static prune(addr: string): string {
    return addr.startsWith("0x") || addr.startsWith("0X")
      ? addr.substring(2).toUpperCase()
      : addr.toUpperCase();
  }

  private static toBinaryString(hexVal: string): string {
    const binForm = bigInt("0x" + hexVal).toString(2);
    const padding = hexVal.length * 4 - binForm.length;
    return "0".repeat(padding) + binForm;
  }

  private static binToHex(binVal: string): string {
    const map: { [key: string]: string } = {
      "0000": "0", "0001": "1", "0010": "2", "0011": "3",
      "0100": "4", "0101": "5", "0110": "6", "0111": "7",
      "1000": "8", "1001": "9", "1010": "A", "1011": "B",
      "1100": "C", "1101": "D", "1110": "E", "1111": "F"
    };
    return map[binVal];
  }

  private static toHexString(binVal: string): string {
    let result = "";
    for (let i = 0; i < binVal.length; i += 4) {
      result += this.binToHex(binVal.slice(i, i + 4));
    }
    return result;
  }

  private static getDevAddrTypePrefix(netID: string): string {
    const netIDBin = this.toBinaryString(netID);
    const netIdPrefix = netIDBin.substring(0, 3);
    const map: { [key: string]: string } = {
      "000": "0", "001": "10", "010": "110", "011": "1110",
      "100": "11110", "101": "111110", "110": "1111110", "111": "11111110"
    };
    return map[netIdPrefix];
  }

  private static getBitsToStripFromDevAddrTypePrefix(prefix: string): number {
    const map: { [key: number]: number } = {
      1: 7, 2: 8, 3: 12, 4: 15, 5: 17, 6: 19, 7: 22, 8: 25
    };
    return map[prefix.length];
  }

  private static getNoOfNwkIdBits(prefix: string): number {
    const map: { [key: string]: number } = {
      "0": 6, "10": 6, "110": 9, "1110": 11,
      "11110": 12, "111110": 13, "1111110": 15, "11111110": 17
    };
    return map[prefix];
  }

  private static getNoOfNwkAddrBits(prefix: string): number {
    const map: { [key: string]: number } = {
      "0": 25, "10": 24, "110": 20, "1110": 17,
      "11110": 15, "111110": 13, "1111110": 10, "11111110": 7
    };
    return map[prefix];
  }

  static getNetworkID(netID: string): string {
    const prefix = this.getDevAddrTypePrefix(this.prune(netID));
    const bits = this.getNoOfNwkIdBits(prefix);
    return this.toBinaryString(this.prune(netID)).slice(-bits);
  }

  static getDevAddrPrefix(netID: string): string {
    const prefix = this.getDevAddrTypePrefix(this.prune(netID));
    const networkID = this.getNetworkID(this.prune(netID));
    return prefix + networkID;
  }

  static getDevAddrRangeStartBinary(netID: string): string {
    return this.getDevAddrPrefix(this.prune(netID)).padEnd(32, "0");
  }

  static getDevAddrRangeEndBinary(netID: string): string {
    return this.getDevAddrPrefix(this.prune(netID)).padEnd(32, "1");
  }

  static getDevAddrRangeStartHex(netID: string): string {
    return this.toHexString(this.getDevAddrRangeStartBinary(netID));
  }

  static getDevAddrRangeEndHex(netID: string): string {
    return this.toHexString(this.getDevAddrRangeEndBinary(netID));
  }

  static getDevAddrRangeFromNetID(netID: string): string {
    const start = this.getDevAddrRangeStartBinary(netID);
    const end = this.getDevAddrRangeEndBinary(netID);
    let index = 0;
    while (start[index] === end[index]) index++;
    return this.toHexString(start) + "/" + index;
  }

  static getNwkAddressBinary(devAddr: string, netID: string): string {
    const prefix = this.getDevAddrTypePrefix(this.prune(netID));
    const binary = this.toBinaryString(this.prune(devAddr));
    const strip = this.getBitsToStripFromDevAddrTypePrefix(prefix);
    return binary.substring(strip);
  }

  static subnetWorkMatches(devAddr: string | null, netID: string, subnwkId: string | null): boolean {
    if (!devAddr || !subnwkId) return false;
    const [prefix, suffix] = subnwkId.split("/");
    const prefixBinary = this.toBinaryString(prefix).substring(0, parseInt(suffix));
    const nwkAddrBinary = this.getNwkAddressBinary(devAddr, netID);
    return nwkAddrBinary.startsWith(prefixBinary);
  }

  static getSubnetRangeFromDevAddrRange(devAddrRange: string, netID: string): string {
    const devAddrPrefix = this.getDevAddrPrefix(this.prune(netID));
    const devAddrSuffix = parseInt(devAddrRange.split("/")[1]);
    const devAddrBinaryStart = this.toBinaryString(this.prune(devAddrRange.split("/")[0])).substring(0, devAddrSuffix).padEnd(32, "0");
    if (!devAddrBinaryStart.startsWith(devAddrPrefix)) {
      throw new Error(
        `DevAddr range ${devAddrRange} is outside the address space of NetID ${netID} ` +
        `(expected DevAddr prefix: ${devAddrPrefix})`
      );
    }
    const subnetStartMSB = devAddrBinaryStart.substring(devAddrPrefix.length);
    const subnetBinaryStart = subnetStartMSB.padEnd(32, "0");

    const devAddrBinaryEnd = this.toBinaryString(this.prune(devAddrRange.split("/")[0])).substring(0, devAddrSuffix).padEnd(32, "1");
    const subnetEndMSB = devAddrBinaryEnd.substring(devAddrPrefix.length);
    const subnetBinaryEnd = subnetEndMSB.padEnd(32, "1");

    let index = 0;
    while (subnetBinaryStart[index] === subnetBinaryEnd[index]) index++;
    return this.toHexString(subnetBinaryStart) + "/" + index;
  }


  static getDevAddrRangeFromSubnetRange(subnetRange: string, netID: string): string {
    const devAddrPrefix = this.getDevAddrPrefix(this.prune(netID));
    const subnetRangeSuffix = parseInt(subnetRange.split("/")[1]);
    const subnetRangeBinaryStart = this.toBinaryString(this.prune(subnetRange.split("/")[0])).substring(0, subnetRangeSuffix);
    const devAddrStart = (devAddrPrefix + subnetRangeBinaryStart).padEnd(32, "0");
    const devAddrEnd = (devAddrPrefix + subnetRangeBinaryStart).padEnd(32, "1");

    let index = 0;
    while (devAddrStart[index] === devAddrEnd[index]) index++;
    return this.toHexString(devAddrStart) + "/" + index;
  }

}
